import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import { MailRibbonTabId, MailRibbonControlId } from 'owa-ribbon-ids';
import {
    createMailGeneratedMenuItem,
    createMailGeneratedMenuFlyoutAnchor,
} from '../../util/createMailRibbonButton';
import type { MenuItemControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/controls/Menu';
import loc from 'owa-localize';
import ReadingPanePosition from 'owa-session-store/lib/store/schema/ReadingPanePosition';
import {
    options_readingPane_quickSetting_right,
    options_readingPane_quickSetting_bottom,
    options_readingPane_quickSetting_classic,
} from 'owa-reading-pane-option/lib/components/ReadingPaneQuick.locstring.json';
import { onReadingPanePositionClicked } from '../../util/commandActions';
import { getUserConfiguration } from 'owa-session-store';
import Strings from 'owa-locstrings/lib/strings/ribbon_mail_buttons_and_tabs.locstring.json';
import { getControlInfo } from 'owa-mail-ribbon-utils';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { shouldShowFolderPane } from 'owa-mail-layout/lib/selectors/shouldShowFolderPane';
import { setShowFolderPane } from 'owa-mail-layout/lib/actions/setShowFolderPane';

export default function getLayoutSections(tabId: MailRibbonTabId): MenuSection[] {
    let sections: MenuItemControlProps[] = [];

    const folderControlInfo = getControlInfo(MailRibbonControlId.FolderPane);
    // add folder menu
    sections.push(
        createMailGeneratedMenuFlyoutAnchor(
            MailRibbonControlId.FolderPane,
            folderControlInfo.text,
            folderControlInfo.tooltip,
            {
                sections: [
                    {
                        controls: getFolderMenuOptions(),
                    },
                ],
            },
            constructKeytip(
                [getKeytipString(tabId), getKeytipString(MailRibbonControlId.Layout)],
                getKeytipString(MailRibbonControlId.FolderPane)
            ),
            false /*isSelected*/,
            folderControlInfo.iconName
        )
    );

    // add reading pane menu
    const readingPaneControlInfo = getControlInfo(MailRibbonControlId.ReadingPane);
    sections.push(
        createMailGeneratedMenuFlyoutAnchor(
            MailRibbonControlId.ReadingPane,
            readingPaneControlInfo.text,
            readingPaneControlInfo.tooltip,
            {
                sections: [
                    {
                        controls: getReadingPaneOptions(),
                    },
                ],
            },
            constructKeytip(
                [getKeytipString(tabId), getKeytipString(MailRibbonControlId.Layout)],
                getKeytipString(MailRibbonControlId.ReadingPane)
            ),
            false /*isSelected*/,
            readingPaneControlInfo.iconName
        )
    );

    return [
        {
            controls: sections,
        },
    ];
}

export function getReadingPaneOptions() {
    let sections: MenuItemControlProps[] = [];

    const currentSelection = getUserConfiguration().UserOptions?.GlobalReadingPanePositionReact;
    const readingPanePositions: {
        position: ReadingPanePosition;
        label: string;
        keytip: string;
    }[] = [
        {
            position: ReadingPanePosition.Right,
            label: loc(options_readingPane_quickSetting_right),
            keytip: 'R',
        },
        {
            position: ReadingPanePosition.Bottom,
            label: loc(options_readingPane_quickSetting_bottom),
            keytip: 'B',
        },
        {
            position: ReadingPanePosition.Off,
            label: loc(options_readingPane_quickSetting_classic),
            keytip: 'H',
        },
    ];

    readingPanePositions.forEach(item => {
        sections.push(
            createMailGeneratedMenuItem(
                MailRibbonControlId.ReadingPane + '_' + item.position,
                item.label,
                undefined /*tooltip*/,
                () => {
                    onReadingPanePositionClicked(item.position);
                },
                constructKeytip(
                    [
                        getKeytipString(MailRibbonTabId.Tab_View),
                        getKeytipString(MailRibbonControlId.ReadingPane),
                    ],
                    item.keytip
                ),
                currentSelection === item.position
            )
        );
    });

    return sections;
}

export function getFolderMenuOptions() {
    const hideLeftPane = !shouldShowFolderPane();
    return [
        createMailGeneratedMenuItem(
            MailRibbonControlId.FolderPane + '_on',
            loc(Strings.folderMenu_Show),
            undefined /*tooltip*/,
            () => {
                setShowFolderPane(true);
            },
            constructKeytip(
                [
                    getKeytipString(MailRibbonTabId.Tab_View),
                    getKeytipString(MailRibbonControlId.FolderPane),
                ],
                Strings.folderMenu_Show[0]
            ),
            !hideLeftPane
        ),
        createMailGeneratedMenuItem(
            MailRibbonControlId.FolderPane + '_off',
            loc(Strings.folderMenu_Hide),
            undefined /*tooltip*/,
            () => {
                setShowFolderPane(false);
            },
            constructKeytip(
                [
                    getKeytipString(MailRibbonTabId.Tab_View),
                    getKeytipString(MailRibbonControlId.FolderPane),
                ],
                Strings.folderMenu_Hide[0]
            ),
            hideLeftPane
        ),
    ];
}
